// NATIVE
import * as React from 'react';

// INSTALLED
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Drawer from '@mui/material/Drawer';
import Divider from '@mui/material/Divider';
import ListItem from '@mui/material/ListItem';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import CssBaseline from '@mui/material/CssBaseline';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { styled, useTheme } from '@mui/material/styles';
import ListItemButton from '@mui/material/ListItemButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';

// CUSTOM
import { useSelector } from 'react-redux';

const drawerWidth = 240;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })<{
  open?: boolean;
}>(({ theme, open }) => ({
  flexGrow: 1,
  // padding: theme.spacing(3),
  justifyContent: 'end',
  display: 'flex',
  zIndex: 999,
  transition: theme.transitions.create('margin', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  marginRight: -264,
  ...(open && {
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: -25,
  }),
  /**
   * This is necessary to enable the selection of content. In the DOM, the stacking order is determined
   * by the order of appearance. Following this rule, elements appearing later in the markup will overlay
   * those that appear earlier. Since the Drawer comes after the Main content, this adjustment ensures
   * proper interaction with the underlying content.
   */
  position: 'relative',
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  transition: theme.transitions.create(['margin', 'width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: drawerWidth,
  }),
}));

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
}));

/**
 * MessagesDrawerRight component displays a right-side drawer that shows a list of messages.
 * The drawer can be toggled open or closed based on the presence of errors.
 *
 * @param {any} props - The properties passed to the component.
 * @param {Array} props.data - The array of messages to be displayed in the drawer.
 *
 * @returns {JSX.Element} The rendered MessagesDrawerRight component.
 *
 * @component
 * @example
 * const data = ["Error 1", "Error 2"];
 * return <MessagesDrawerRight data={data} />;
 *
 * @remarks
 * This component uses Material-UI components such as Box, CssBaseline, Drawer, List, ListItem, etc.
 * It also uses the `useTheme` hook to access the theme and `useSelector` to access the Redux state.
 *
 * @function handleDrawerClose - Closes the drawer.
 * @function handleDrawer - Toggles the drawer open or closed.
 *
 * @hook useEffect - Sets the drawer open state based on the `icon_request_has_errors` value.
 */
export const MessagesDrawerRight = (props: any) => {
  // USE THEME
  const theme = useTheme();

  //  PROPS
  const { data } = props;

  // USE SELECTOR
  const { has_errors, icon_request_has_errors } = useSelector((state: any) => state.errors);

  // USE STATE
  const [open, setOpen] = React.useState(false);

  // CONST FUNCTIONS
  const handleDrawerClose = () => {
    setOpen(false);
  };

  const handleDrawer = () => {
    setOpen(!open);
  };

  React.useEffect(() => {
    setOpen(icon_request_has_errors);
  }, [icon_request_has_errors]);

  if (data && data.length <= 0) {
    return <></>;
  }

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      <Main open={open}>
        {icon_request_has_errors && (
          <div
            onClick={handleDrawer}
            style={{
              padding: 8,
              background: '#FFFF',
              borderEndStartRadius: 20,
              borderStartStartRadius: 20,
              display: 'flex',
              cursor: 'pointer',
              position: 'fixed',
            }}
          >
            <NotificationImportantIcon fontSize={'medium'} color="error" />
          </div>
        )}
      </Main>

      <Drawer
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            backgroundColor: '#FFFF',
            color: 'black',
          },
        }}
        variant="persistent"
        anchor="right"
        open={open}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === 'rtl' ? <ChevronLeftIcon /> : <ChevronRightIcon />}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List>
          <ListItem key={'key'} disablePadding>
            <ListItemButton>
              <ListItemIcon></ListItemIcon>
              <ListItemText primary={'Atencion'} />
              <CloseIcon onClick={handleDrawerClose} />
            </ListItemButton>
          </ListItem>
        </List>
        <Divider />
        <List>
          {data.map((text: any, index: any) => (
            <>
              <ListItem key={text} disablePadding>
                <ListItemButton>
                  <ListItemIcon>
                    <ReportProblemIcon color="warning" />
                  </ListItemIcon>
                  <ListItemText primary={text} />
                </ListItemButton>
              </ListItem>
              <Divider />
            </>
          ))}
        </List>
      </Drawer>
    </Box>
  );
};
